import React from "react";
import SidePanel, { PanelHeader } from "./side-panel";
import { useIsMobile } from "../hooks/use-is-mobile";
import { Button } from "./ui/button";
import { X } from "lucide-react";
import { ScrollArea } from "./ui/scroll-area";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import SelectInput, { SelectOption } from "./select-input";
import { RadioGroupCards } from "./ui/radio-group-cards";
import AutoSuggestion from "./auto-suggestion";

interface FormDrawerProps {
  title: string;
  buttonText: string;
  formValues: { [key: string]: unknown } | null;
  setFormValues: React.Dispatch<
    React.SetStateAction<{ [key: string]: unknown } | null>
  >;
  fields: Field[];
  isSubmitting: boolean;
  handleSubmit: () => void;
  onClose: () => void;
}

export interface Field {
  key: string;
  label: string;
  type: string;
  /** Native input type for text fields, e.g. "number". */
  inputType?: React.HTMLInputTypeAttribute;
  placeholder?: string;
  className?: string;
  isRequired: boolean;
  options?: SelectOption[];
  suggestions?: string[];
}

const FormDrawer: React.FC<FormDrawerProps> = ({
  title,
  buttonText,
  formValues,
  setFormValues,
  fields,
  isSubmitting,
  handleSubmit,
  onClose,
}) => {
  const isMobile = useIsMobile();

  return (
    <SidePanel open={!!formValues} onClose={onClose} width="w-[700px]">
      {formValues && (
        <div className="relative h-full flex flex-col">
          {isMobile ? (
            <PanelHeader title={title} onClose={onClose} />
          ) : (
            <div className="px-6 py-4 flex-shrink-0 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-[22px] font-semibold text-foreground font-jakarta mr-auto">
                  {title}
                </h2>
                <div className="flex items-center gap-2">
                  <Button
                    disabled={isSubmitting}
                    className="mr-3"
                    onClick={handleSubmit}
                  >
                    {buttonText}
                  </Button>
                  <Button
                    variant="outline"
                    className="h-8 w-8 p-0 flex-shrink-0"
                    onClick={onClose}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
          <div className="flex-grow overflow-hidden">
            <ScrollArea className="h-full">
              <div
                className={isMobile ? "space-y-5 p-4 pb-8" : "space-y-6 p-6"}
              >
                {fields.map((item, index) => {
                  if (item.type === "radio-group") {
                    return (
                      <div key={index} className="space-y-2">
                        <Label className="font-medium">{item.label}</Label>
                        <RadioGroupCards
                          className={item.className}
                          options={item.options ?? []}
                          value={formValues[item.key] as string}
                              onChange={(value) => {
                                setFormValues((current) => ({
                                  ...(current ?? {}),
                                  [item.key]: value,
                                }));
                              }}
                            />
                      </div>
                    );
                  }

                  if (item.type === "select") {
                    return (
                      <div key={index} className="space-y-2">
                        <Label htmlFor={item.key} className="font-medium">
                          {item.label}
                        </Label>
                        <SelectInput
                          value={formValues[item.key] as string}
                          options={item.options ?? []}
                          placeholder={item.placeholder}
                              onChange={(value) => {
                                setFormValues((current) => ({
                                  ...(current ?? {}),
                                  [item.key]: value,
                                }));
                              }}
                              className="w-full"
                        />
                      </div>
                    );
                  }

                  if (item.type === "auto-suggestion") {
                    return (
                      <div key={index} className="space-y-2">
                        <Label htmlFor={item.key} className="font-medium">
                          {item.label}
                        </Label>
                        <AutoSuggestion
                          value={(formValues[item.key] as string) ?? ""}
                          suggestions={item?.suggestions ?? []}
                          placeholder={item.placeholder}
                              onSelect={(value) => {
                                setFormValues((current) => ({
                                  ...(current ?? {}),
                                  [item.key]: value,
                                }));
                              }}
                        />
                      </div>
                    );
                  }

                  return (
                    <div key={index} className="space-y-2">
                      <Label htmlFor={item.key} className="font-medium">
                        {item.label}
                      </Label>
                      <Input
                        id={item.key}
                        type={item.inputType}
                        inputMode={
                          item.inputType === "number" ? "decimal" : undefined
                        }
                        step={item.inputType === "number" ? "any" : undefined}
                        value={
                          formValues[item.key] == null
                            ? ""
                            : String(formValues[item.key])
                        }
                        onChange={(event) => {
                          const next = event.target.value;
                          setFormValues((current) => ({
                            ...(current ?? {}),
                            [item.key]: next,
                          }));
                        }}
                        placeholder={item.placeholder}
                      />
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
          {isMobile && (
            <div className="shrink-0 border-t border-gray-100 bg-white px-4 py-3">
              <Button
                disabled={isSubmitting}
                className="h-12 w-full rounded-2xl text-[15px] font-semibold"
                onClick={handleSubmit}
              >
                {isSubmitting ? "Saving…" : buttonText}
              </Button>
            </div>
          )}
        </div>
      )}
    </SidePanel>
  );
};

export default FormDrawer;
